import { For, Show, type Component } from 'solid-js';

import { joinPartyMember, kickFromParty, type PartyMember } from '../net/protocol';
import type { GameStore } from '../state/store';
import { percent } from './VitalOrb';

/**
 * The party: up to four member cards down the left edge.
 *
 * ── What it is for ──────────────────────────────────────────────────────────
 *
 * **Answering *is anyone dying* without looking away from the fight.** That is the whole
 * job, and it is why each card is a name and a health bar rather than a row of stats: a
 * number you have to read is a number you will not read while something is hitting you.
 *
 * The left edge because everything else is taken — the minimap and the test button are
 * top-right, the panel rail is right, the orbs and the action bar are bottom-centre, and
 * the town bar is top-centre. It is also the side a right-handed player's cursor is least
 * often on.
 *
 * ── Where the numbers come from ─────────────────────────────────────────────
 *
 * The **party frame**, which arrives only when membership changes. Health on it is a
 * snapshot of that moment, so it would go stale — except that party members are ordinary
 * entities in the 30 Hz snapshot the client already has, and this reads *that* when it can.
 * The frame's own figure is the fallback for a member who is not in the snapshot, which
 * happens when they are in a different zone.
 */
export const PartyBar: Component<{ store: GameStore }> = (props) => {
  const members = () => props.store.party().members ?? [];
  const isLeader = () => props.store.party().leaderId === props.store.selfId();

  /**
   * A member's live health, preferring the snapshot.
   *
   * The party frame is sent on membership changes, so its numbers are as old as the last
   * one. Entities in the snapshot are 30 Hz — and a member in another zone is not in it at
   * all, which is exactly when the frame's figure is the only one there is.
   */
  const vitals = (member: PartyMember) => {
    const live = props.store.entities().find((e) => e.id === member.id);
    return live ? { hp: live.hp, maxHp: live.maxHp } : { hp: member.hp, maxHp: member.maxHp };
  };

  return (
    <Show when={members().length > 0}>
      <div class="party hud-interactive" data-testid="party-bar" aria-label="Party">
        <For each={members()}>
          {(member) => {
            const self = () => member.id === props.store.selfId();
            const hp = () => vitals(member);
            const fraction = () => percent(hp().hp, hp().maxHp);

            return (
              <div
                class="party__card"
                classList={{
                  'party__card--self': self(),
                  // Below a quarter, because that is the point at which somebody needs
                  // help rather than merely having taken a hit.
                  'party__card--hurt': fraction() < 25,
                  'party__card--dead': hp().hp <= 0,
                }}
                data-testid={`party-member-${member.id}`}
              >
                {/* **The portrait is the way to them, on a double click.**
                    Going to a member takes you to their zone — which is how a party plays
                    together at all, since everybody dives alone. Never on your own card: you
                    are already where you are, and a button that could only do nothing is
                    worse than no button.

                    ── It used to be the whole card, on a double click ─────────────────

                    The argument was that "the person you want to be with" is the mental model,
                    so the card should be the target, and that a 200-pixel *teleport across the
                    server* under a cursor also used to walk and fight needed a double click to
                    mean *I meant that*.

                    **Both halves were wrong together, and each one hid the other.** Asked for
                    directly: *he needs to click on a portal icon beside his party mate, instead
                    of clicking on the nameplate.* An invisible overlay with no icon and no
                    single-click behaviour is a feature nobody can find — it announced itself
                    only in a `title` attribute, on a card that looked like a readout. The
                    double click was compensating for a hit target that was far too big; a small
                    marked button does not need compensating for, so it is a single click again.

                    Kick keeps its own button for the same reason this one now has one: an action
                    deserves something to press. */}
                <div class="party__head">
                  <Show when={!self()}>
                    <button
                      type="button"
                      class="party__goto"
                      data-testid={`party-goto-${member.id}`}
                      aria-label={`Travel to ${member.name}`}
                      title={`Travel to ${member.name}`}
                      onClick={() => props.store.send(joinPartyMember(member.id))}
                    >
                      ⌾
                    </button>
                  </Show>
                  <Show when={member.leader}>
                    {/* The leader picks the depth and launches the run. Marked rather than
                        ordered differently, so the cards keep a stable order — a party
                        whose rows reshuffled would be unreadable at a glance. */}
                    <span class="party__crown" title="Party leader" aria-label="Party leader">
                      ★
                    </span>
                  </Show>
                  <span class="party__name">{member.name || 'Unnamed'}</span>
                  <span class="party__level">{member.level}</span>
                </div>

                <div
                  class="party__bar"
                  role="progressbar"
                  aria-label={`${member.name} health`}
                  aria-valuenow={hp().hp}
                  aria-valuemin={0}
                  aria-valuemax={hp().maxHp}
                >
                  <div class="party__fill" style={{ width: `${fraction()}%` }} />
                </div>

                {/* Leader only, and never on yourself — leaving is the *Leave* button, and
                    a kick that could target you would be the same action with a worse
                    name. The server re-checks both. */}
                <Show when={isLeader() && !self()}>
                  <button
                    type="button"
                    class="party__kick"
                    data-testid={`party-kick-${member.id}`}
                    aria-label={`Remove ${member.name} from the party`}
                    title={`Remove ${member.name}`}
                    onClick={() => props.store.send(kickFromParty(member.id))}
                  >
                    ✕
                  </button>
                </Show>
              </div>
            );
          }}
        </For>
      </div>
    </Show>
  );
};
